import React, { useState, useEffect } from 'react';
import { BarChart3, Building, FileText, Users, TrendingUp, Search } from 'lucide-react';
import { auth, db } from '../../config/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useToast } from '../../components/ui/ToastProvider';
import { H1, H2, H3 } from '../../components/typography';
import { useNavigate } from 'react-router-dom';

export default function Reports() {
  const [associations, setAssociations] = useState([]);
  const [housing, setHousing] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      // Hämta alla collections parallellt
      const [associationsSnapshot, housingSnapshot, applicationsSnapshot] = await Promise.all([
        getDocs(collection(db, 'associations')),
        getDocs(collection(db, 'housing')),
        getDocs(collection(db, 'applications'))
      ]);

      const associationsData = associationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const housingData = housingSnapshot.docs.map(doc => doc.data());
      const applicationsData = applicationsSnapshot.docs.map(doc => doc.data());

      setAssociations(associationsData);
      setHousing(housingData);
      setApplications(applicationsData);
    } catch (error) {
      toast({ type: 'error', message: 'Kunde inte hämta rapportdata' });
    } finally {
      setLoading(false);
    }
  };

  // Beräkna KPI:er
  const totalAssociations = associations.length;
  const totalApplications = applications.length;
  const activeApplications = applications.filter(app => 
    app.status && !['REJECTED', 'EXPIRED', 'COMPLETED', 'FINAL_REJECTED'].includes(app.status.toUpperCase())
  ).length;
  const completedApplications = totalApplications - activeApplications;

  // Beräkna statistik per förening
  const associationStats = associations.map(association => {
    const associationHousing = housing.filter(h => h.associationId === association.id);
    const associationApplications = applications.filter(a => a.associationId === association.id);
    const activeAssociationApplications = associationApplications.filter(app => 
      app.status && !['REJECTED', 'EXPIRED', 'COMPLETED', 'FINAL_REJECTED'].includes(app.status.toUpperCase())
    );

    const totalApartments = associationHousing.length;
    const totalApps = associationApplications.length;
    const activeApps = activeAssociationApplications.length;
    const usageRate = totalApartments > 0 ? Math.round((totalApps / totalApartments) * 100) : 0;

    return {
      id: association.id,
      name: association.name,
      organizationNumber: association.organizationNumber,
      totalApartments,
      totalApplications: totalApps,
      activeApplications: activeApps,
      usageRate,
      status: association.status
    };
  });

  // Filtrera baserat på sökning
  const filteredStats = associationStats.filter(stat =>
    stat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stat.organizationNumber.includes(searchTerm)
  );

  // Status-fördelning
  const statusDistribution = {
    PENDING: applications.filter(a => a.status === 'PENDING').length,
    APPROVED: applications.filter(a => a.status === 'APPROVED').length,
    ACTIVE: applications.filter(a => a.status === 'ACTIVE').length,
    REJECTED: applications.filter(a => a.status === 'REJECTED').length,
    CREDIT_CHECK_COMPLETE: applications.filter(a => a.status === 'CREDIT_CHECK_COMPLETE').length,
    TENANT_INFO_SUBMITTED: applications.filter(a => a.status === 'TENANT_INFO_SUBMITTED').length
  };

  const maxStatusCount = Math.max(...Object.values(statusDistribution));

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow">
                <div className="h-6 bg-gray-200 rounded mb-2"></div>
                <div className="h-8 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8">
      <H1 className="mb-6">Rapporter</H1>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
            <Building className="w-6 h-6 text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-zinc-800 mb-1">{totalAssociations}</div>
          <div className="text-sm text-zinc-500 text-center">Föreningar</div>
        </div>

        <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
            <FileText className="w-6 h-6 text-green-600" />
          </div>
          <div className="text-2xl font-bold text-zinc-800 mb-1">{totalApplications}</div>
          <div className="text-sm text-zinc-500 text-center">Ansökningar totalt</div>
        </div>

        <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center">
          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-3">
            <TrendingUp className="w-6 h-6 text-orange-600" />
          </div>
          <div className="text-2xl font-bold text-zinc-800 mb-1">{activeApplications}</div>
          <div className="text-sm text-zinc-500 text-center">Aktiva ansökningar</div>
        </div>

        <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center">
          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-3">
            <BarChart3 className="w-6 h-6 text-purple-600" />
          </div>
          <div className="text-2xl font-bold text-zinc-800 mb-1">{completedApplications}</div>
          <div className="text-sm text-zinc-500 text-center">Avslutade ansökningar</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Föreningstabell */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <H2 className="flex items-center gap-2">
                <Building className="w-5 h-5 text-blue-600" />
                Föreningsaktivitet
              </H2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Sök föreningar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-zinc-500 border-b border-zinc-200">
                    <th className="text-left py-3 pr-4 font-medium">Förening</th>
                    <th className="text-left py-3 pr-4 font-medium">Org.nr</th>
                    <th className="text-left py-3 pr-4 font-medium">Lägenheter</th>
                    <th className="text-left py-3 pr-4 font-medium">Ansökningar</th>
                    <th className="text-left py-3 pr-4 font-medium">Aktiva</th>
                    <th className="text-left py-3 pr-4 font-medium">Användning</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStats.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-8 text-zinc-500">
                        {searchTerm ? 'Inga föreningar matchar din sökning' : 'Inga föreningar hittades'}
                      </td>
                    </tr>
                  ) : (
                    filteredStats
                      .sort((a, b) => b.totalApplications - a.totalApplications) // Sortera efter aktivitet
                      .map((stat) => (
                        <tr 
                          key={stat.id} 
                          className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer transition"
                          onClick={() => navigate(`/hyria-dashboard/associations`)}
                        >
                          <td className="py-3 pr-4">
                            <div className="font-medium text-zinc-800">{stat.name}</div>
                            <div className="flex items-center gap-1 mt-1">
                              <span className={`w-2 h-2 rounded-full ${
                                stat.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                              }`}></span>
                              <span className="text-xs text-zinc-500">
                                {stat.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 pr-4 text-zinc-600 font-mono text-xs">
                            {stat.organizationNumber}
                          </td>
                          <td className="py-3 pr-4 text-zinc-700 font-medium">
                            {stat.totalApartments}
                          </td>
                          <td className="py-3 pr-4 text-zinc-700 font-medium">
                            {stat.totalApplications}
                          </td>
                          <td className="py-3 pr-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              stat.activeApplications > 0 
                                ? 'bg-orange-100 text-orange-800' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {stat.activeApplications}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div 
                                  className={`h-2 rounded-full ${
                                    stat.usageRate > 50 ? 'bg-green-500' :
                                    stat.usageRate > 20 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${Math.min(stat.usageRate, 100)}%` }}
                                ></div>
                              </div>
                              <span className="text-xs font-medium text-zinc-600 min-w-[35px]">
                                {stat.usageRate}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>

            {filteredStats.length > 0 && (
              <div className="mt-4 text-sm text-zinc-500">
                Visar {filteredStats.length} av {associationStats.length} föreningar
              </div>
            )}
          </div>
        </div>

        {/* Status-fördelning */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow p-6">
            <H2 className="mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              Ansökningsstatus
            </H2>
            
            <div className="space-y-3">
              {Object.entries(statusDistribution).map(([status, count]) => {
                const percentage = maxStatusCount > 0 ? (count / maxStatusCount) * 100 : 0;
                const statusLabels = {
                  PENDING: 'Väntar BRF',
                  APPROVED: 'Godkänd',
                  ACTIVE: 'Aktiv',
                  REJECTED: 'Avvisad',
                  CREDIT_CHECK_COMPLETE: 'Väntar beslut',
                  TENANT_INFO_SUBMITTED: 'Väntar kreditkontroll'
                };
                
                const statusColors = {
                  PENDING: 'bg-yellow-500',
                  APPROVED: 'bg-green-500',
                  ACTIVE: 'bg-blue-500',
                  REJECTED: 'bg-red-500',
                  CREDIT_CHECK_COMPLETE: 'bg-purple-500',
                  TENANT_INFO_SUBMITTED: 'bg-orange-500'
                };

                return (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1">
                      <div className={`w-3 h-3 rounded-full ${statusColors[status]}`}></div>
                      <span className="text-sm text-zinc-700">{statusLabels[status]}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${statusColors[status]}`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-zinc-800 min-w-[20px]">
                        {count}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 pt-4 border-t border-zinc-200">
              <div className="text-xs text-zinc-500 mb-2">Sammanfattning</div>
              <div className="text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-600">Totalt:</span>
                  <span className="font-medium">{totalApplications}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-600">Aktiva:</span>
                  <span className="font-medium text-orange-600">{activeApplications}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-600">Avslutade:</span>
                  <span className="font-medium text-green-600">{completedApplications}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

